import { Logo } from "@/components/logo";
import { useIsTouch } from "@/hooks/useIsTouch";

import { cn } from "@repo/design-system/utils";
import { Link, type LinkProps } from "@tanstack/react-router";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
  useTransform
} from "framer-motion";
import {
  PanelLeftClose,
  PanelLeftOpen,
  X,
  type LucideProps
} from "lucide-react";
import React, { createContext, useContext, useRef, useState } from "react";

interface SidebarContextProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  animate: boolean;
  mouseX: any;
}

const SidebarContext = createContext<SidebarContextProps | undefined>(
  undefined
);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export const SidebarProvider = ({
  children,
  open: openProp,
  setOpen: setOpenProp,
  animate = true
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  const [openState, setOpenState] = useState(false);
  const mouseX = useMotionValue(Infinity);

  const open = openProp !== undefined ? openProp : openState;
  const setOpen = setOpenProp !== undefined ? setOpenProp : setOpenState;

  return (
    <SidebarContext.Provider value={{ open, setOpen, animate, mouseX }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const Sidebar = ({
  children,
  open,
  setOpen,
  animate
}: {
  children: React.ReactNode;
  open?: boolean;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  animate?: boolean;
}) => {
  return (
    <SidebarProvider open={open} setOpen={setOpen} animate={animate}>
      {children}
    </SidebarProvider>
  );
};

export const SidebarBody = (props: React.ComponentProps<typeof motion.div>) => {
  return (
    <>
      <DesktopSidebar {...props} />
      <MobileSidebar {...(props as React.ComponentProps<"div">)} />
    </>
  );
};

export const DesktopSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof motion.div>) => {
  const { open, setOpen, animate, mouseX } = useSidebar();
  // The rail expands on hover, which is a gesture a finger does not have. On a
  // touch tablet - the `md`/`lg` device this epic is aimed at - that left the
  // labels unreachable, so touch gets an explicit toggle instead and the mouse
  // handlers are dropped rather than left to fire on a stray synthetic hover.
  // See storefront#145 (P4).
  const isTouch = useIsTouch();
  const pointerHandlers = isTouch
    ? {}
    : {
        onMouseEnter: () => setOpen(true),
        onMouseLeave: () => {
          setOpen(false);
          mouseX.set(Infinity);
        },
        onMouseMove: (e: React.MouseEvent) => {
          mouseX.set(e.pageX);
        }
      };
  return (
    <div
      className={cn(
        "hidden h-full flex-shrink-0 bg-neutral-100 md:flex md:flex-col dark:bg-neutral-800",
        className
      )}
    >
      <Link
        to="/"
        className="m-2 flex items-center justify-start gap-2 focus-visible:outline-none"
      >
        <Logo variant="desktop" />
        {open && (
          <span className="text-xl font-bold text-gray-900 dark:text-white">
            SalonStocker
          </span>
        )}
      </Link>
      <motion.div
        animate={{
          width: animate ? (open ? "176px" : "56px") : "300px"
        }}
        transition={{ duration: 0 }}
        {...pointerHandlers}
        className="p-4 pt-0"
        {...props}
      >
        {isTouch && (
          <button
            type="button"
            onClick={() => setOpen(!open)}
            aria-expanded={open}
            aria-label={open ? "Collapse navigation" : "Expand navigation"}
            className="group/sidebar mb-1 flex min-h-[44px] w-full items-center justify-start gap-2 py-2"
          >
            <SidebarIcon Icon={open ? PanelLeftClose : PanelLeftOpen} />
            <motion.span
              animate={{
                display: open ? "inline-block" : "none",
                opacity: open ? 1 : 0
              }}
              className="!m-0 inline-block whitespace-pre !p-0 text-sm text-neutral-700 dark:text-neutral-200"
            >
              Collapse
            </motion.span>
          </button>
        )}
        {/* `motion.div` widens its `children` to include MotionValue, which is
            not a valid JSX child once it shares a list with the button above. */}
        {children as React.ReactNode}
      </motion.div>
    </div>
  );
};

/**
 * The phone nav drawer - and only the drawer. It used to also render a
 * `h-10 w-full` bar carrying the logo and a hamburger; that bar now lives in
 * the header's phone row, which had space for it, so the shell isn't spending
 * a band on two controls. `md:hidden` stays on the panel itself because `open`
 * is shared with the desktop rail's hover-expand - without it, hovering the
 * rail on a laptop would throw this full-screen panel over the page.
 * See storefront#145 (P4).
 */
export const MobileSidebar = ({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) => {
  const { open, setOpen } = useSidebar();
  return (
    // A zero-height flex child that holds the portal-less panel. Kept as a
    // plain `div` so the component's `ComponentProps<"div">` contract still
    // has somewhere to land - `motion.div` types several DOM handlers
    // differently and would reject the spread.
    <div {...props}>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "-100%", opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: "-100%", opacity: 0 }}
            transition={{
              duration: 0.3,
              ease: "easeInOut"
            }}
            // Following any link closes the drawer, so the buyer lands on the
            // page they picked instead of on a panel still covering it.
            onClick={(event) => {
              if ((event.target as HTMLElement).closest("a")) {
                setOpen(false);
              }
            }}
            className={cn(
              "fixed inset-0 z-[100] flex h-full w-full flex-col justify-between bg-white p-4 md:hidden dark:bg-neutral-900",
              className
            )}
          >
            <div className="flex items-center justify-between">
              <Link
                to="/"
                className="flex items-center justify-start gap-2 text-xl font-bold text-gray-900 dark:text-white"
              >
                <Logo variant="mobile" />
                SalonStocker
              </Link>
              <button
                type="button"
                aria-label="Close menu"
                className="-mr-2 flex h-11 w-11 items-center justify-center rounded-lg text-neutral-800 dark:text-neutral-200"
                onClick={() => setOpen(false)}
              >
                <X />
              </button>
            </div>
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

type SidebarLinkProps = {
  link: SidebarLinkItem;
  className?: string;
} & Omit<LinkProps<"a">, "to">;

export const SidebarLink = ({
  link,
  className,
  ...props
}: SidebarLinkProps) => {
  const { open, animate, mouseX } = useSidebar();
  const ref = useRef<HTMLAnchorElement>(null);

  // Dock-style effect similar to the dock component
  const distance = 140; // Max distance for effect
  const magnification = 60; // Max magnification size
  const spring = { mass: 0.1, stiffness: 150, damping: 12 };

  const mouseDistance = useTransform(mouseX, (val: number) => {
    const domRect = ref.current?.getBoundingClientRect() ?? { x: 0, width: 0 };
    return val - domRect.x - domRect.width / 2;
  });

  const widthTransform = useTransform(
    mouseDistance,
    [-distance, 0, distance],
    [20, magnification, 20]
  );

  const width = useSpring(widthTransform, spring);
  const iconScale = useTransform(width, [20, magnification], [1, 2.5]);
  const iconScaleSpring = useSpring(iconScale, spring);

  if (link.comingSoon) {
    // Not a link: the page doesn't exist yet, so there is nothing to navigate to.
    return (
      <div
        aria-disabled="true"
        className={cn(
          "relative flex cursor-not-allowed items-center justify-start gap-2 py-2 opacity-60",
          className
        )}
      >
        <SidebarIcon Icon={link.Icon} />
        <motion.span
          animate={{
            display: animate ? (open ? "inline-flex" : "none") : "inline-flex",
            opacity: animate ? (open ? 1 : 0) : 1
          }}
          className="!m-0 inline-flex items-center gap-2 whitespace-pre !p-0 text-sm text-neutral-700 dark:text-neutral-200"
        >
          {link.label}
          <span className="rounded-full bg-purple-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
            Soon
          </span>
        </motion.span>
      </div>
    );
  }

  return (
    <Link
      ref={ref}
      to={link.to}
      className={cn(
        "group/sidebar relative flex items-center justify-start gap-2 py-2",
        className
      )}
      {...props}
    >
      <SidebarIcon Icon={link.Icon} />
      <motion.span
        animate={{
          display: animate ? (open ? "inline-block" : "none") : "inline-block",
          opacity: animate ? (open ? 1 : 0) : 1
        }}
        className="!m-0 inline-block whitespace-pre !p-0 text-sm text-neutral-700 transition duration-150 group-hover/sidebar:translate-x-1 dark:text-neutral-200"
      >
        {link.label}
      </motion.span>
    </Link>
  );
};

export interface SidebarLinkItem {
  label: string;
  to: LinkProps<"a">["to"];
  Icon: SideBarIcon;
  /** Renders a muted, non-clickable entry with a "Soon" badge instead of a link. */
  comingSoon?: boolean;
}

type SideBarIcon = React.ForwardRefExoticComponent<
  Omit<LucideProps, "ref"> & React.RefAttributes<SVGSVGElement>
>;

type SidebarIconProps = LucideProps &
  React.RefAttributes<SVGSVGElement> & {
    Icon: SideBarIcon;
  };
export const SidebarIcon = ({
  Icon,
  className,
  ...props
}: SidebarIconProps) => {
  return (
    <Icon
      className={cn(
        "h-5 w-5 flex-shrink-0 text-neutral-700 dark:text-neutral-200",
        className
      )}
      {...props}
    />
  );
};
